import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { captureTouch, trackAcq } from "@/lib/acquisition";

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/features", label: "Features" },
  { to: "/tools", label: "Free Tools" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export const PLANS = [
  { name: "Starter", stage: "Just Starting", price: 29.99, priceAnnual: 299.99, tag: "Launch your first business",
    bestFor: "Aspiring and first-time founders turning an idea into an organized business.",
    outcome: "Get set up right — formation, documents, contracts, and an AI advisor guiding each step.",
    limits: "150 AI messages/mo · 25 documents · 500 MB storage",
    features: ["Business Formation", "AI Business Advisor", "Document Library", "Basic Contracts", "Progress Tracker"] },
  { name: "Growth", stage: "Building & Growing", price: 79.99, priceAnnual: 799.99, tag: "Scale with credit & funding", featured: true,
    bestFor: "Founders past the idea stage who need credit, funding, and stronger systems to grow.",
    outcome: "Become fundable and get paid — credit building, funding readiness, advanced contracts, and business analytics.",
    limits: "1,500 AI messages/mo · 200 documents · 5 GB storage",
    features: ["Everything in Starter", "Business Credit Center", "Funding Center", "Advanced Contracts + DOCX export", "Business analytics & reports"] },
  { name: "Enterprise", stage: "Scaling", price: 199.99, priceAnnual: 1999.99, tag: "Full operating system",
    bestFor: "Established teams and agencies running operations, clients, and data at scale.",
    outcome: "Run the whole operation with your team — seats, a client portal, multi-business management, and enterprise-grade security.",
    limits: "Unlimited AI & documents · 50 GB storage · up to 10 seats",
    features: ["Everything in Growth", "Team accounts & roles (up to 10)", "Client Portal", "Multi-business (up to 5)", "Enterprise security & audit logs"] },
];

const LEGAL = [
  ["/legal/terms", "Terms of Service", "terms"],
  ["/legal/privacy", "Privacy Policy", "privacy"],
  ["/legal/refunds", "Refund & Cancellation", "refunds"],
  ["/legal/cookies", "Cookie Policy", "cookies"],
  ["/legal/acceptable-use", "Acceptable Use", "acceptable-use"],
];

// Lightweight SEO for the SPA: sets title, description and OG tags per page.
export function Seo({ title, description, path }) {
  useEffect(() => {
    const full = `${title} | Fairview Systems`;
    document.title = full;
    const set = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.replace(/meta\[|\]/g, "").split("=");
        el.setAttribute(k, v.replace(/["']/g, ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    set('meta[name="description"]', "content", description);
    set('meta[property="og:title"]', "content", full);
    set('meta[property="og:description"]', "content", description);
    set('meta[property="og:type"]', "content", "website");
    set('meta[property="og:site_name"]', "content", "Fairview Systems");
    set('meta[property="og:image"]', "content", `${window.location.origin}/og-image.png`);
    set('meta[property="og:url"]', "content", `${window.location.origin}${path || ""}`);
    set('meta[name="twitter:card"]', "content", "summary_large_image");
    set('meta[name="twitter:title"]', "content", full);
    set('meta[name="twitter:description"]', "content", description);
    set('meta[name="twitter:image"]', "content", `${window.location.origin}/og-image.png`);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", `${window.location.origin}${path || ""}`);
  }, [title, description, path]);
  return null;
}

export function PublicNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => { trackAcq("free_tools_nav_impression"); }, []);

  return (
    <header className="fixed top-0 inset-x-0 z-50 fv-glass">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <div className="w-9 h-9 rounded-lg fv-gold-gradient flex items-center justify-center"><span className="font-heading text-[#0A0A0B] text-xl font-bold">F</span></div>
          <div className="leading-tight"><div className="font-heading text-lg tracking-wide">Fairview</div><div className="text-[9px] tracking-[0.25em] uppercase text-[#D4AF37]">Systems</div></div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="public-nav">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => l.to === "/tools" && trackAcq("free_tools_nav_click")}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${pathname === l.to ? "text-[#D4AF37]" : "text-[#A1A1AA] hover:text-white"}`}>{l.label}</Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate("/demo")} data-testid="nav-demo-btn"
            className="text-[#A1A1AA] hover:text-white hover:bg-white/5">Interactive Demo</Button>
          <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn"
            className="text-[#A1A1AA] hover:text-white hover:bg-white/5">Log in</Button>
          <Button onClick={() => navigate("/register?plan=growth")} data-testid="nav-trial-btn"
            className="fv-gold-gradient text-[#0A0A0B] hover:opacity-90 font-medium rounded-full px-5">Start Free Trial</Button>
        </div>

        <button onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle"
          className="lg:hidden w-10 h-10 rounded-lg hover:bg-white/5 flex items-center justify-center text-white">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/8 bg-[#0A0A0B]/95 backdrop-blur px-6 py-4 space-y-1" data-testid="mobile-menu">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`mnav-${l.label.toLowerCase()}`}
              onClick={() => l.to === "/tools" && trackAcq("free_tools_nav_click")}
              className="block px-3 py-2.5 text-sm text-[#D4D4D8] hover:text-white rounded-lg hover:bg-white/5">{l.label}</Link>
          ))}
          <div className="pt-3 flex flex-col gap-2">
            <Button variant="outline" onClick={() => navigate("/demo")} className="border-[#D4AF37]/40 bg-transparent text-white rounded-full">Interactive Demo</Button>
            <Button onClick={() => navigate("/register?plan=growth")} className="fv-gold-gradient text-[#0A0A0B] font-medium rounded-full">Start Free Trial</Button>
            <Button variant="ghost" onClick={() => navigate("/login")} className="text-[#A1A1AA]">Log in</Button>
          </div>
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="border-t border-white/8 py-12 px-6" data-testid="public-footer">
      <div className="max-w-7xl mx-auto grid gap-10 md:grid-cols-5">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-md fv-gold-gradient flex items-center justify-center"><span className="font-heading text-[#0A0A0B] font-bold">F</span></div>
            <span className="font-heading text-white text-base">Fairview Systems</span>
          </div>
          <p className="text-sm text-[#71717A] max-w-xs">The all-in-one business operating system to launch, fund, and grow your company.</p>
        </div>
        <div>
          <div className="text-white text-sm font-medium mb-3">Product</div>
          <ul className="space-y-2 text-sm text-[#71717A]">
            <li><Link to="/features" className="hover:text-[#D4AF37]">Features</Link></li>
            <li><Link to="/pricing" className="hover:text-[#D4AF37]">Pricing</Link></li>
            <li><Link to="/demo" className="hover:text-[#D4AF37]">Interactive Demo</Link></li>
            <li><Link to="/blog" className="hover:text-[#D4AF37]">Blog & Resources</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white text-sm font-medium mb-3">Free Tools</div>
          <ul className="space-y-2 text-sm text-[#71717A]" data-testid="footer-tools-links">
            <li><Link to="/tools" data-testid="footer-tools-index" className="hover:text-[#D4AF37]">Free Business Tools</Link></li>
            <li><Link to="/tools/business-name-generator" className="hover:text-[#D4AF37]">Business Name Generator</Link></li>
            <li><Link to="/tools/business-idea-validator" className="hover:text-[#D4AF37]">Business Idea Validator</Link></li>
            <li><Link to="/tools/business-plan-generator" className="hover:text-[#D4AF37]">Business Plan Generator</Link></li>
            <li><Link to="/tools/business-structure-recommender" className="hover:text-[#D4AF37]">LLC vs. S-Corp</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white text-sm font-medium mb-3">Company</div>
          <ul className="space-y-2 text-sm text-[#71717A]">
            <li><Link to="/about" className="hover:text-[#D4AF37]">About</Link></li>
            <li><Link to="/faq" className="hover:text-[#D4AF37]">FAQ</Link></li>
            <li><Link to="/contact" className="hover:text-[#D4AF37]">Contact</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white text-sm font-medium mb-3">Legal</div>
          <ul className="space-y-2 text-sm text-[#71717A]" data-testid="footer-legal-links">
            {LEGAL.map(([to, label, slug]) => (
              <li key={slug}><Link to={to} data-testid={`footer-legal-${slug}`} className="hover:text-[#D4AF37]">{label}</Link></li>
            ))}
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-10 pt-6 border-t border-white/5 text-xs text-[#71717A]">© 2026 Fairview Systems. All rights reserved.</div>
    </footer>
  );
}

export function PublicPage({ children }) {
  useEffect(() => { captureTouch(); }, []);
  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white overflow-x-hidden">
      <PublicNav />
      <main>{children}</main>
      <PublicFooter />
    </div>
  );
}

export function PageHero({ eyebrow, title, subtitle }) {
  const navigate = useNavigate();
  return (
    <section className="pt-36 pb-14 px-6 fv-grain border-b border-white/8">
      <div className="max-w-4xl mx-auto text-center">
        {eyebrow && <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">{eyebrow}</div>}
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4">{title}</h1>
        {subtitle && <p className="text-base sm:text-lg text-[#A1A1AA] max-w-2xl mx-auto">{subtitle}</p>}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
          <Button onClick={() => navigate("/register?plan=growth")} data-testid="hero-trial-btn"
            className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-7 h-11">Start Free Trial <ArrowRight className="w-4 h-4 ml-2" /></Button>
          <Button variant="outline" onClick={() => navigate("/demo")} data-testid="hero-demo-btn"
            className="border-[#D4AF37]/40 bg-transparent text-white hover:bg-white/5 rounded-full px-7 h-11">Explore Interactive Demo</Button>
        </div>
      </div>
    </section>
  );
}
